
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";

// Search cities async thunk
export const searchCities = createAsyncThunk(
  "city/searchCities",
  async (query, { rejectWithValue }) => {
    try {
      const response = await api.searchCities(query);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to search cities",
      );
    }
  },
);

// Find city by slug async thunk - automatically selects the city
export const findCityBySlug = createAsyncThunk(
  "city/findCityBySlug",
  async (slug, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.findCityBySlug(slug);
      const city = response.data || response;

      // Auto-select the city
      if (city && city.id) {
        dispatch(setSelectedCity(city));
      }

      return city;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to find city",
      );
    }
  },
);

const getInitialCity = () => {
  try {
    const cityData = localStorage.getItem("selected_city");

    if (cityData) {
      const city = JSON.parse(cityData);

      if (city && city.id) {
        return city;
      }
    }

    return null;
  } catch {
    return null;
  }
};

const initialState = {
  selectedCity: getInitialCity(),
  cityModalVisible: false,
  isCityChanging: false,
  // Search states
  searchResults: [],
  searching: false,
  searchError: null,
  searchTerm: "",
  // Find by slug states
  foundCity: null,
  findingCity: false,
  findCityError: null,
};

const citySlice = createSlice({
  name: "city",
  initialState,
  reducers: {
    setSelectedCity: (state, action) => {
      const city = action.payload;
      if (city && city.id) {
        state.selectedCity = city;
        localStorage.setItem("selected_city", JSON.stringify(city));
      }
    },
    openCityModal: (state) => {
      state.cityModalVisible = true;
      state.searchTerm = "";
      state.searchResults = [];
      state.searchError = null;
    },
    closeCityModal: (state) => {
      state.cityModalVisible = false;
      state.searchTerm = "";
      state.searchResults = [];
      state.searchError = null;
    },
    startCityChange: (state) => {
      state.isCityChanging = true;
    },
    endCityChange: (state) => {
      state.isCityChanging = false;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchTerm = "";
      state.searchError = null;
    },
    clearFoundCity: (state) => {
      state.foundCity = null;
      state.findCityError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Search cities
      .addCase(searchCities.pending, (state) => {
        state.searching = true;
        state.searchError = null;
      })
      .addCase(searchCities.fulfilled, (state, action) => {
        state.searching = false;
        const data = action.payload;
        state.searchResults = Array.isArray(data) ? data : data.data || [];
        state.searchError = null;
      })
      .addCase(searchCities.rejected, (state, action) => {
        state.searching = false;
        state.searchResults = [];
        state.searchError = action.payload || "Failed to search cities";
      })
      // Find city by slug
      .addCase(findCityBySlug.pending, (state) => {
        state.findingCity = true;
        state.findCityError = null;
        state.foundCity = null;
      })
      .addCase(findCityBySlug.fulfilled, (state, action) => {
        state.findingCity = false;
        state.foundCity = action.payload;
        state.findCityError = null;
        // City is already auto-selected via dispatch in the thunk
      })
      .addCase(findCityBySlug.rejected, (state, action) => {
        state.findingCity = false;
        state.foundCity = null;
        state.findCityError = action.payload || "Failed to find city";
      });
  },
});

export const {
  setSelectedCity,
  openCityModal,
  closeCityModal,
  startCityChange,
  endCityChange,
  setSearchTerm,
  clearSearchResults,
  clearFoundCity,
} = citySlice.actions;

export default citySlice.reducer;
